import type { NewCoApplicantCardData } from '@shared/new-co-applicant-card/new-co-applicant-card.model';
import type {
  ExistingCustomerAddressHit,
  ExistingCustomerHit,
  FetchExistingCustomerRequest,
} from '../../models/opportunity.model';
import { coerceText } from '../../utils/coerce-text';
import { normalizeDateForApi } from '../../utils/normalize-date-for-api.util';
import { normalizeExistingCustomerSearchFormValues } from '../../utils/existing-customer-search-validation.util';

export {
  normalizeExistingCustomerSearchFormValues,
  validateExistingCustomerSearchRequest,
} from '../../utils/existing-customer-search-validation.util';

function str(value: unknown): string | undefined {
  const trimmed = coerceText(value ?? '').trim();
  return trimmed || undefined;
}

function requiredStr(value: unknown): string {
  return coerceText(value ?? '').trim();
}

function normalizeSearchDate(value: unknown): string | undefined {
  return normalizeDateForApi(value);
}

function nullableStr(value: unknown): string | null {
  const trimmed = coerceText(value ?? '').trim();
  return trimmed || null;
}

function parseCustomerType(value: unknown): ExistingCustomerHit['customerType'] {
  return coerceText(value).toUpperCase() === 'C' ? 'C' : 'I';
}

function parseAddresses(value: unknown): ExistingCustomerAddressHit[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const addresses = value
    .filter((item) => item && typeof item === 'object')
    .map((item) => {
      const row = item as Record<string, unknown>;
      return {
        addressLine1: requiredStr(row['addressLine1']),
        addressLine2: str(row['addressLine2']),
        pinCode: str(row['pinCode']),
        city: str(row['city']),
        district: str(row['district']),
        stateCode: str(row['stateCode']),
        countryCode: str(row['countryCode']),
        addressTypeCode: str(row['addressTypeCode']),
      };
    })
    .filter((item) => item.addressLine1);
  return addresses.length ? addresses : undefined;
}

/** Normalize proxy/API collection rows into typed existing-customer hits. */
export function parseExistingCustomerHits(
  rows: Record<string, unknown>[],
): ExistingCustomerHit[] {
  return rows.map((row) => ({
    id: str(row['id']),
    customerId: requiredStr(row['customerId']),
    customerName: requiredStr(row['customerName']),
    customerDob: requiredStr(row['customerDob']),
    customerType: parseCustomerType(row['customerType']),
    constitution: requiredStr(row['constitution']),
    applicationFormNumber: requiredStr(row['applicationFormNumber']),
    opportunityNumber: requiredStr(row['opportunityNumber']),
    loanNo: nullableStr(row['loanNo']),
    aadharNo: requiredStr(row['aadharNo']),
    panNumber: requiredStr(row['panNumber']),
    voterIdNumber: requiredStr(row['voterIdNumber']),
    dlNumber: requiredStr(row['dlNumber']),
    passportNumber: requiredStr(row['passportNumber']),
    tinNo: requiredStr(row['tinNo']),
    cinNo: nullableStr(row['cinNo']),
    dateOfInCorporation: requiredStr(row['dateOfInCorporation']),
    mobileNumber1: requiredStr(row['mobileNumber1']),
    mobileNumber2: nullableStr(row['mobileNumber2']),
    linkCustomerId: Number(row['linkCustomerId']) || 0,
    customerRoleType: requiredStr(row['customerRoleType']),
    loaneffactiovDate: requiredStr(row['loaneffactiovDate']),
    customerTypeDesc: requiredStr(row['customerTypeDesc']),
    genderCode: str(row['genderCode']),
    emailId: str(row['emailId']),
    address: str(row['address']),
    addresses: parseAddresses(row['addresses']),
  }));
}

/** Drop empty optional fields so the API body matches the search modal UI only. */
export function compactFetchExistingCustomerRequest(
  request: FetchExistingCustomerRequest,
): FetchExistingCustomerRequest {
  const out: FetchExistingCustomerRequest = {
    applicantType: request.applicantType,
  };

  const optionalKeys: Array<keyof Omit<FetchExistingCustomerRequest, 'applicantType'>> = [
    'customerName',
    'customerDob',
    'aadharNumber',
    'panNumber',
    'contactNumber',
    'organizationName',
    'dateOfInCorporation',
    'gstNumber',
    'incorporationRegistrationNumber',
    'passportNumber',
    'drivingLicenceNumber',
    'voterIdNumber',
    'excludePartyId',
  ];

  for (const key of optionalKeys) {
    const value = request[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      if (key === 'customerDob' || key === 'dateOfInCorporation') {
        const normalized = normalizeSearchDate(value);
        if (normalized) {
          out[key] = normalized;
        }
        continue;
      }
      out[key] = String(value).trim();
    }
  }

  return out;
}

/** Read the first non-empty search field value from pack form raw values. */
export function readExistingCustomerSearchField(
  values: Record<string, unknown>,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = str(values[key]);
    if (value) {
      return value;
    }
  }
  return undefined;
}

/** Map pack search form values → GET /common/fetch-existing-customer query params. */
export function buildFetchExistingCustomerRequest(
  applicantType: 'individual' | 'corporate',
  values: Record<string, unknown>,
): FetchExistingCustomerRequest {
  const normalized = normalizeExistingCustomerSearchFormValues(
    applicantType,
    values,
  );
  if (applicantType === 'corporate') {
    return compactFetchExistingCustomerRequest({
      applicantType: 'corporate',
      organizationName: readExistingCustomerSearchField(normalized, [
        'searchByOrganizationName',
        'textField',
        'organizationName',
      ]),
      dateOfInCorporation: readExistingCustomerSearchField(normalized, [
        'dateOfIncorporation',
        'date',
      ]),
      panNumber: readExistingCustomerSearchField(normalized, ['panNumber', 'textField2']),
      gstNumber: readExistingCustomerSearchField(normalized, [
        'gSTNumber',
        'gstNumber',
        'textField3',
      ]),
      incorporationRegistrationNumber: readExistingCustomerSearchField(normalized, [
        'incorporationRegistrationNumber',
        'textField4',
      ]),
    });
  }

  return compactFetchExistingCustomerRequest({
    applicantType: 'individual',
    customerName: readExistingCustomerSearchField(normalized, [
      'searchByName',
      'textField',
      'name',
      'customerName',
    ]),
    customerDob: readExistingCustomerSearchField(normalized, [
      'dateOfBirth',
      'date',
      'dob',
      'customerDob',
    ]),
    aadharNumber: readExistingCustomerSearchField(normalized, [
      'aadharNumber',
      'textField2',
      'aadhar',
    ]),
    panNumber: readExistingCustomerSearchField(normalized, ['panNumber', 'textField3', 'pan']),
    contactNumber: readExistingCustomerSearchField(normalized, [
      'contactNumber',
      'phone',
      'contact',
    ]),
  });
}

/** Map API/search request → pack dataset query template vars (`{{textField}}`, etc.). */
export function buildCustomerSearchPackVars(
  request: FetchExistingCustomerRequest,
): Record<string, string> {
  if (request.applicantType === 'corporate') {
    return {
      applicantType: 'corporate',
      textField: request.organizationName ?? '',
      date: request.dateOfInCorporation ?? '',
      textField2: request.panNumber ?? '',
      textField3: request.gstNumber ?? '',
      textField4: request.incorporationRegistrationNumber ?? '',
    };
  }

  return {
    applicantType: 'individual',
    searchByName: request.customerName ?? '',
    textField: request.customerName ?? '',
    dateOfBirth: request.customerDob ?? '',
    date: request.customerDob ?? '',
    aadharNumber: request.aadharNumber ?? '',
    textField2: request.aadharNumber ?? '',
    panNumber: request.panNumber ?? '',
    textField3: request.panNumber ?? '',
    contactNumber: request.contactNumber ?? '',
    phone: request.contactNumber ?? '',
  };
}

/** Repopulate the existing-customer search pack form after navigating back from results. */
export function existingCustomerSearchFormPatchFromQueryParams(
  params: Record<string, string>,
): Record<string, unknown> {
  const request = buildFetchExistingCustomerFromQueryParams(params);
  if (request.applicantType === 'corporate') {
    return {
      searchByOrganizationName: request.organizationName ?? '',
      organizationName: request.organizationName ?? '',
      dateOfIncorporation: request.dateOfInCorporation ?? '',
      panNumber: request.panNumber ?? '',
      gSTNumber: request.gstNumber ?? '',
      gstNumber: request.gstNumber ?? '',
      incorporationRegistrationNumber:
        request.incorporationRegistrationNumber ?? '',
    };
  }

  return {
    searchByName: request.customerName ?? '',
    name: request.customerName ?? '',
    date: request.customerDob ?? '',
    dateOfBirth: request.customerDob ?? '',
    dob: request.customerDob ?? '',
    aadharNumber: request.aadharNumber ?? '',
    panNumber: request.panNumber ?? '',
    contactNumber: request.contactNumber ?? '',
    phone: request.contactNumber ?? '',
  };
}

/** Rebuild API request from existing-customer-search route query params. */
export function buildFetchExistingCustomerFromQueryParams(
  params: Record<string, string>,
): FetchExistingCustomerRequest {
  const applicantType = params['applicantType'] === 'corporate' ? 'corporate' : 'individual';

  if (applicantType === 'corporate') {
    return compactFetchExistingCustomerRequest({
      applicantType,
      organizationName: str(params['organizationName']),
      dateOfInCorporation: str(params['dateOfIncorporation']),
      panNumber: str(params['panNumber']),
      gstNumber: str(params['gstNumber']),
      incorporationRegistrationNumber: str(params['incorporationRegistrationNumber']),
    });
  }

  return compactFetchExistingCustomerRequest({
    applicantType,
    customerName: str(params['name']),
    customerDob: str(params['dob']),
    aadharNumber: str(params['aadhar']),
    panNumber: str(params['pan']),
    contactNumber: str(params['contact']),
  });
}

export function hasFetchExistingCustomerCriteria(
  request: FetchExistingCustomerRequest,
): boolean {
  const { applicantType: _type, ...criteria } = request;
  return Object.values(criteria).some(Boolean);
}

/** True when the existing-customer dialog search was submitted (not just route landing). */
export function isExistingCustomerSearchSubmitted(
  params: Record<string, string>,
): boolean {
  return params['searchSubmitted'] === 'true';
}

/** Gate proxy fetch: dialog submitted and at least one search field is present. */
export function shouldFetchExistingCustomers(
  params: Record<string, string>,
): boolean {
  if (!isExistingCustomerSearchSubmitted(params)) {
    return false;
  }
  return hasFetchExistingCustomerCriteria(
    buildFetchExistingCustomerFromQueryParams(params),
  );
}

function assignDefinedQueryParams(
  params: Record<string, string>,
  entries: Array<[string, string | undefined]>,
): void {
  for (const [key, value] of entries) {
    if (value) {
      params[key] = value;
    }
  }
}

/** Route query params for existing-customer-search navigation. */
export function buildExistingCustomerSearchQueryParams(
  request: FetchExistingCustomerRequest,
  applicantStatus: string | null | undefined,
): Record<string, string> {
  const params: Record<string, string> = {
    applicantType: request.applicantType,
    applicantStatus: applicantStatus ?? 'existing',
  };

  const fieldEntries: Array<[string, string | undefined]> =
    request.applicantType === 'corporate'
      ? [
          ['organizationName', request.organizationName],
          ['dateOfIncorporation', request.dateOfInCorporation],
          ['panNumber', request.panNumber],
          ['gstNumber', request.gstNumber],
          ['incorporationRegistrationNumber', request.incorporationRegistrationNumber],
        ]
      : [
          ['name', request.customerName],
          ['dob', request.customerDob],
          ['aadhar', request.aadharNumber],
          ['pan', request.panNumber],
          ['contact', request.contactNumber],
        ];

  assignDefinedQueryParams(params, fieldEntries);
  params['searchSubmitted'] = 'true';
  return params;
}

export function mapExistingCustomerHitToCard(
  hit: ExistingCustomerHit,
): NewCoApplicantCardData {
  return {
    name: hit.customerName || '—',
    customerId: hit.customerId,
    mobile: hit.mobileNumber1 || '—',
    address: hit.address?.trim() ? hit.address : '—',
    pan: hit.panNumber || '—',
    type: hit.customerType === 'C' ? 'Corporate' : 'Individual',
    image: 'assets/images/profile-1.jpeg',
  };
}

export { mapExistingCustomerHitToSelectedRow } from './pack-form-customer-prefill.mapper';
